import { motion } from "framer-motion";
import { Mail, Phone, MapPin } from "lucide-react";
import { CONTACT } from "@/data";

export const Contact = () => {
  return (
    <section
      id="contact"
      className="relative bg-slate-900 pt-24 md:pt-36 overflow-hidden"
      data-testid="contact-section"
    >
      <div className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 h-96 w-[40rem] rounded-full bg-sky-500/25 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-5xl px-5 text-center">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-block font-display font-bold text-sky-400 uppercase tracking-widest text-sm mb-5"
        >
          Ready when you are
        </motion.span>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-black tracking-tighter text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.85]"
        >
          GET IN <span className="text-sky-400">TOUCH</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="mt-6 text-lg md:text-xl text-slate-300 font-medium max-w-2xl mx-auto"
        >
          Send us an email or give us a call — we will be glad to book your
          child in and answer any questions you have.
        </motion.p>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-5">
          <motion.a
            href={`mailto:${CONTACT.email}`}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            data-testid="contact-email-button"
            className="group inline-flex items-center gap-3 rounded-full bg-sky-400 px-8 py-5 font-display font-bold text-lg text-slate-900 shadow-[0_16px_40px_rgba(56,189,248,0.4)] hover:bg-sky-300 transition-colors w-full sm:w-auto justify-center"
          >
            <Mail className="h-6 w-6" />
            {CONTACT.email}
          </motion.a>
          <motion.a
            href={CONTACT.phoneHref}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            data-testid="contact-phone-button"
            className="group inline-flex items-center gap-3 rounded-full border-2 border-white/30 bg-white/5 px-8 py-5 font-display font-bold text-lg text-white hover:bg-white hover:text-slate-900 transition-colors w-full sm:w-auto justify-center"
          >
            <Phone className="h-6 w-6" />
            {CONTACT.phone}
          </motion.a>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15 }}
          data-testid="contact-phone-note"
          className="mt-4 text-sm font-semibold text-slate-400"
        >
          {CONTACT.phone} is our in-camp phone for parents — call or text us
          any time during camp.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mt-10 inline-flex items-center gap-2 text-slate-400 font-semibold"
        >
          <MapPin className="h-5 w-5 text-sky-400" />
          {CONTACT.location}
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 mt-24 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img
              src="/images/logo.png"
              alt="Action Packed Adventures logo"
              className="h-10 w-10 rounded-full object-cover"
            />
            <span className="font-display font-extrabold text-white text-lg">
              Action Packed <span className="text-sky-400">Adventures</span>
            </span>
          </div>

          <div className="text-center md:text-right text-slate-400 text-sm font-medium">
            <p>
              &copy; {new Date().getFullYear()} Action Packed Adventures. All
              rights reserved.
            </p>
            <p className="mt-1">
              UK Registered Company Number:{" "}
              <span className="text-slate-300 font-bold">
                {CONTACT.company}
              </span>
            </p>
          </div>
        </div>
      </footer>
    </section>
  );
};

export default Contact;
